import React, { useContext } from 'react';

import { downloadUrl } from '@apis/common';
import selectFiles from '@chenyuwang/select-files';
import { BookingDetailsContext } from '@components/admin/booking/helpers';
import { Button } from '@components/Button';
import { QueryContainer } from '@components/QueryContainer';
import { cargoTrackingStrings } from '@data/cargoTracking';
import { usePatchTrackingData as useDashboardPatchTrackingData } from '@hooks/mutation/dashboard/usePatchTrackingData';
import { useTrackingData as useDashboardTrackingData } from '@hooks/queries/dashboard/useTrackingData';
import { useFormUtils } from '@hooks/useFormUtils';
import { AdminInquiryDetailHelper } from '@models/BookingInquiry';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { mapQueriesData } from '@utils/types';
import clsx from 'clsx';

const strings = createStrings({
  ja: {
    uploadSeaWaybill: 'Upload Sea Waybill',
    viewSeaWaybill: 'View Sea Waybill',
    statusNote: 'Sea Waybillがアップロードされました。',
  },
});

const useStyles = makeMuiStyles((_theme, _common) => ({
  seaWaybillFormContainer: {
    padding: '1rem 1rem 3.5rem 1rem',
  },
}));

export const SeaWaybillForm2: React.FC = () => {
  const classes = {
    ...useStyles(),
    ...useCommonStyles(),
  };

  const { bookingInquiry, preAlert } = useContext(BookingDetailsContext);

  const trackingDataQuery = useDashboardTrackingData(bookingInquiry.d2d_no);
  const patchTrackingData = useDashboardPatchTrackingData(bookingInquiry.d2d_no);

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [patchTrackingData],
    formAlertProps: {
      gutter: true,
    },
  });

  const canCreate = AdminInquiryDetailHelper.canCreateSeaWaybill(preAlert);

  const renderForm = () => (
    <QueryContainer queries={[trackingDataQuery]}>
      {mapQueriesData({ trackingData: trackingDataQuery }, ({ trackingData }) => {
        if ('detail' in trackingData) return null;

        const targetStatus = trackingData.data[cargoTrackingStrings.seaWaybillGenerated]?.find(
          (status) => (status.file?.length ?? 0) > 0,
        );

        return (
          <div className={classes.flexInline} style={{ columnGap: '1rem' }}>
            {targetStatus && (
              <Button
                color="secondary"
                shape="round"
                text={strings.viewSeaWaybill}
                onClick={async () => {
                  await downloadUrl({
                    url: targetStatus?.file?.[0]!,
                    name: 'Sea Waybill',
                    download: false,
                  });
                }}
              />
            )}
            <Button
              color="secondary"
              shape="round"
              text={strings.uploadSeaWaybill}
              onClick={async () => {
                const uploadFiles = await selectFiles({
                  accept: 'application/pdf',
                  multiple: false,
                });

                if (!uploadFiles || [...uploadFiles].length === 0) return;

                await patchTrackingData.mutateAsync({
                  transport_status: cargoTrackingStrings.seaWaybillGenerated,
                  place: '',
                  time: new Date().toISOString(),
                  coop_name: undefined,
                  coop_tracking_no: undefined,
                  car_no: undefined,
                  car_driver: undefined,
                  file: [...uploadFiles],
                  note: strings.statusNote,
                  key: targetStatus?.key,
                });
              }}
            />
          </div>
        );
      })}
    </QueryContainer>
  );

  return (
    <div className={classes.seaWaybillFormContainer}>
      {renderAlertsAndSpinnerModal()}
      {canCreate._tag === 'Right' ? (
        renderForm()
      ) : (
        <div className={clsx(classes.middleText, classes.textGray600)}>
          {canCreate.left.messages.map((m) => (
            <div key={m}>{m}</div>
          ))}
        </div>
      )}
    </div>
  );
};
